import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminHeader from "@/components/AdminHeader";
import { api, eur, kindLabel } from "@/lib/api";
import { StatusBadge } from "@/pages/AdminDashboard";
import { toast } from "sonner";
import { CalendarDays, Phone, Mail } from "lucide-react";

export default function AdminPlanning() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/admin/planning`, { params: { days } })
      .then((r) => setData(r.data.days))
      .catch(() => toast.error("Chargement impossible"))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-planning-page">
      <AdminHeader />
      <main className="max-w-6xl mx-auto pt-28 pb-16 px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.5em] gold uppercase">Opérations</div>
            <h1 className="mt-2 font-serif text-white text-4xl inline-flex items-center gap-3">
              <CalendarDays className="gold" size={30} /> Planning
            </h1>
            <p className="mt-2 text-gray-400 text-sm">
              Vos prestations à venir, groupées par jour. Idéal pour l'imprimer et l'emporter.
            </p>
          </div>
          <div className="flex gap-2">
            {[7, 14, 30].map((d) => (
              <button
                key={d}
                data-testid={`planning-range-${d}`}
                onClick={() => setDays(d)}
                className={`text-xs px-4 py-2 border tracking-wide transition-all ${
                  days === d
                    ? "bg-[color:var(--gold)] text-black border-[color:var(--gold)]"
                    : "border-white/10 text-gray-400 hover:border-white/30"
                }`}
              >
                {d}j
              </button>
            ))}
            <button
              data-testid="planning-print"
              onClick={() => window.print()}
              className="text-xs px-4 py-2 border border-white/10 text-gray-400 hover:border-white/30 tracking-wide"
            >
              Imprimer
            </button>
          </div>
        </div>

        {loading && <div className="mt-10 text-gray-500">Chargement...</div>}

        {!loading && data.length === 0 && (
          <div className="mt-16 border border-white/10 bg-[#0a0a0a] p-16 text-center text-gray-500">
            Aucune prestation planifiée sur les {days} prochains jours.
          </div>
        )}

        <div className="mt-8 space-y-8">
          {data.map((day) => (
            <section
              key={day.date}
              data-testid={`planning-day-${day.date}`}
              className="border border-white/10 bg-[#0a0a0a]"
            >
              <header className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500">Journée</div>
                  <div className="font-serif text-2xl text-white capitalize">
                    {formatDayLabel(day.date)}
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {day.bookings.length} prestation{day.bookings.length > 1 ? "s" : ""}
                </div>
              </header>
              <ul className="divide-y divide-white/5">
                {day.bookings.map((b) => (
                  <li key={b.id} className="p-6">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <Link
                            to={`/admin/bookings/${b.id}`}
                            className="gold tracking-widest hover:underline"
                          >
                            {b.reference}
                          </Link>
                          <span className="text-white">{kindLabel(b.booking_type)}</span>
                          <StatusBadge status={b.status} />
                        </div>
                        <div className="mt-2 text-sm text-gray-400">
                          {getRouteSummary(b)}
                        </div>
                        <div className="mt-3 flex items-center flex-wrap gap-4 text-sm">
                          <span className="text-white">{b.client.full_name}</span>
                          <a href={`tel:${b.client.phone}`} className="text-gray-400 hover:gold inline-flex items-center gap-1">
                            <Phone size={12} /> {b.client.phone}
                          </a>
                          <a href={`mailto:${b.client.email}`} className="text-gray-400 hover:gold inline-flex items-center gap-1">
                            <Mail size={12} /> {b.client.email}
                          </a>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-serif text-2xl text-white">{eur(b.price.total)}</div>
                        <div className="text-xs text-gray-500">
                          {b.payment_status === "paid" ? "payé" : b.payment_status}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}

function formatDayLabel(iso) {
  const d = new Date(iso + "T12:00:00");
  const today = new Date();
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const dt = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = Math.round((dt - t) / (1000 * 60 * 60 * 24));
  const long = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  if (diff === 0) return `Aujourd'hui — ${long}`;
  if (diff === 1) return `Demain — ${long}`;
  return long;
}

function getRouteSummary(b) {
  if (b.convoyage) {
    return `${b.convoyage.pickup?.label || ""} → ${b.convoyage.dropoff?.label || ""} · ${b.convoyage.brand} ${b.convoyage.model}`;
  }
  if (b.vtc_trip) {
    return `${b.vtc_trip.pickup?.label || ""} → ${b.vtc_trip.dropoff?.label || ""}`;
  }
  if (b.vtc_dispo) {
    return `Départ ${b.vtc_dispo.pickup?.label || ""} · ${b.vtc_dispo.duration_hours}h`;
  }
  return "";
}
